import React, { useEffect } from 'react';
import { PublicLayout } from '@/components/public';
import { BRAND } from '@/config/brand';
import { SOLUTIONS_FOOTER_COLUMN } from '@/config/verticals';

import VerticalHero from './verticals/VerticalHero';
import AnswerFirstBox from './verticals/AnswerFirstBox';
import ValueBlock from './verticals/ValueBlock';
import VerticalFaq from './verticals/VerticalFaq';
import SpokeNav from './verticals/SpokeNav';
import InlineLink from './verticals/InlineLink';

const PAGE_TITLE =
  'Ghost Kitchens & Food Pop-Ups Online Ordering | Hungry Canadian';
const PAGE_DESCRIPTION =
  'Multi-brand virtual food halls and pop-up concepts online in 1–2 days. Unified brand grids, single-tablet dispatch, flat-rate pricing, and multi-brand KDS syncing.';

const NAV_PROPS = {
  links: [
    { href: '#multi-brand', label: 'Multi-brand', testId: 'ghost-nav-multi-brand' },
    { href: '#rapid-launch', label: 'Rapid launch', testId: 'ghost-nav-launch' },
    { href: '#driver-coordination', label: 'Driver ops', testId: 'ghost-nav-driver' },
    { href: '#faq', label: 'FAQ', testId: 'ghost-nav-faq' },
  ],
  primaryCta: { href: '/#lead-form-anchor', label: 'Get Started', testId: 'ghost-nav-cta' },
};

const FOOTER_PROPS = {
  tagline:
    'A Canadian-owned delivery platform that keeps more of every sale with the local businesses that feed our communities.',
  columns: [
    {
      title: 'For Restaurants',
      links: [
        { href: '/', label: 'Why Hungry Canadian', testId: 'ghost-footer-why' },
        { href: '/tools', label: 'Tools', testId: 'ghost-footer-tools' },
        { href: '/features', label: 'Features', testId: 'ghost-footer-features' },
        { href: '/integrations', label: 'Integrations', testId: 'ghost-footer-integrations' },
      ],
    },
    SOLUTIONS_FOOTER_COLUMN,
    {
      title: 'Resources',
      links: [
        { href: 'https://help.hungrycanadian.com/', label: 'Help center', external: true, testId: 'ghost-footer-help' },
        { href: `mailto:${BRAND.merchantsEmail}`, label: 'Contact merchants', external: true, testId: 'ghost-footer-contact' },
      ],
    },
  ],
  legal: [
    { href: 'https://help.hungrycanadian.com/tos/', label: 'Terms of Service', external: true, testId: 'ghost-footer-tos' },
    { href: 'https://help.hungrycanadian.com/privacypolicy/', label: 'Privacy Policy', external: true, testId: 'ghost-footer-privacy' },
  ],
};

// Cross-link href constants (SSOT for this page)
const HREF_PRICING = '/#pricing';
const HREF_INTEGRATIONS_HUB = '/integrations';
// Cross-spoke sibling links
const HREF_RESTAURANTS = '/for-restaurants';
const HREF_CAFES_BAKERIES = '/for-cafes-bakeries';

// -----------------------------------------------------------------------------
// FAQ content
// -----------------------------------------------------------------------------
const GHOST_FAQ = [
  {
    id: 'multi-brand-single-checkout',
    q: 'Can customers order from multiple virtual brands in a single transaction?',
    a: (
      <>
        Yes. Hungry Canadian builds custom brand collection grids that allow customers to order
        items from multiple concepts operating in the same ghost kitchen within a single checkout
        experience.
      </>
    ),
    plainAnswer:
      'Yes. Hungry Canadian builds custom brand collection grids that allow customers to order items from multiple concepts operating in the same ghost kitchen within a single checkout experience.',
  },
  {
    id: 'launch-speed',
    q: 'How quickly can a ghost kitchen or food pop-up launch on Hungry Canadian?',
    a: (
      <>
        New stores can be fully configured and ready to accept orders in 1 to 2 business days
        using flat-rate pricing and AI-assisted menu import tools.
      </>
    ),
    plainAnswer:
      'New stores can be fully configured and ready to accept orders in 1 to 2 business days using flat-rate pricing and AI-assisted menu import tools.',
  },
];

const FAQ_SCHEMA = GHOST_FAQ.map(({ q, plainAnswer }) => ({ q, plainAnswer }));

// -----------------------------------------------------------------------------
// Value prop block content
// -----------------------------------------------------------------------------
const BLOCK_MULTI_BRAND = {
  eyebrow: 'One kitchen, many brands',
  title: 'Multi-Brand Aggregation on One Device',
  blurb:
    'A single ghost kitchen commonly runs 3–7 virtual brands. Hungry Canadian treats them as first-class citizens on one storefront and one dispatch surface — not a tangle of separate tablets.',
  bullets: [
    {
      label: 'Custom Brand Collection Grids',
      detail:
        'Present all your virtual brands on a unified digital storefront. Families can order items from your taco brand, burger concept, and dessert pop-up simultaneously in a single checkout.',
    },
    {
      label: 'Single-Tablet Kitchen Dispatch',
      detail:
        'Manage incoming orders for every virtual brand from a single dashboard—eliminating multi-tablet clutter on the pass.',
    },
    {
      label: (
        <>
          <InlineLink href={HREF_PRICING} testId="block-multibrand-link-pro">
            Pro Connect
          </InlineLink>{' '}
          POS Syncing
        </>
      ),
      detail: (
        <>
          Unify multiple brand menus and inventory across your entire{' '}
          <InlineLink href={HREF_INTEGRATIONS_HUB} testId="block-multibrand-link-kds">
            KDS (Kitchen Display System)
          </InlineLink>{' '}
          automatically.
        </>
      ),
    },
  ],
};

const BLOCK_RAPID_LAUNCH = {
  eyebrow: 'Speed & low overhead',
  title: 'Rapid Launch & Low Capital Risk',
  blurb:
    'Virtual brands live or die on speed-to-launch and unit economics. Skip the 6-week onboarding queues and the 30% commission drag that kills most concepts before they find their audience.',
  bullets: [
    {
      label: '1–2 Day Speed to Market',
      detail:
        'Get new pop-up concepts online and accepting orders in as little as 24–48 hours.',
    },
    {
      label: 'Zero Commission Gouging',
      detail: (
        <>
          <InlineLink href={HREF_PRICING} testId="block-launch-link-pricing">
            Fixed monthly pricing
          </InlineLink>{' '}
          gives virtual brands predictable overhead metrics, making short-term pop-ups highly
          profitable.
        </>
      ),
    },
    {
      label: 'Instant Menu Engineering',
      detail: (
        <>
          Launch{' '}
          <InlineLink href={HREF_RESTAURANTS} testId="block-launch-link-ltos">
            LTOs (Limited Time Offers)
          </InlineLink>{' '}
          or test new culinary concepts in minutes using the AI Menu Builder and instant toggles.
        </>
      ),
    },
  ],
};

const BLOCK_DRIVER_OPS = {
  eyebrow: 'Dispatch & driver ops',
  title: 'Streamlined Driver Coordination & Item Verification',
  blurb:
    'The busiest ghost kitchens fire 200+ tickets an hour across half a dozen brands. Hungry Canadian gives your expo line the exact visibility and driver-facing controls to keep every order accurate.',
  bullets: [
    {
      label: 'Unified Order IDs',
      detail:
        'Track every ticket across all brands using clean, privacy-compliant Order IDs (Customer First Name + Order ID).',
    },
    {
      label: 'Driver Packing Verification',
      detail:
        'Drivers using the Hungry Canadian network must confirm itemized packing checklists before departing the facility.',
    },
    {
      label: 'Custom Facility Instructions',
      detail:
        'Provide specific pickup notes for drivers (e.g., "Pick up at rear alley door #3") to prevent dispatch confusion.',
    },
    {
      label: 'Time-Stamped Progress Tracking',
      detail:
        'Track live order stages from accepted to prepared, picked up, and delivered with complete operational transparency.',
    },
  ],
};

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
export default function ForGhostKitchensPopupsPage() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = PAGE_TITLE;

    let meta = document.querySelector('meta[name="description"]');
    const prevDescription = meta?.getAttribute('content');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', PAGE_DESCRIPTION);

    return () => {
      document.title = prevTitle;
      if (prevDescription != null) meta.setAttribute('content', prevDescription);
    };
  }, []);

  return (
    <PublicLayout navProps={NAV_PROPS} footerProps={FOOTER_PROPS}>
      <div data-testid="for-ghost-kitchens-popups-page">
        <VerticalHero
          eyebrow="For Ghost Kitchens & Pop-ups"
          h1="Turnkey Digital Ordering for Ghost Kitchens & Food Pop-Ups"
          tagline={
            <>
              Launch, manage, and scale multiple{' '}
              <InlineLink href={HREF_CAFES_BAKERIES} testId="hero-tagline-link-virtual-brands">
                virtual food brands
              </InlineLink>{' '}
              from a single kitchen footprint&mdash;without multi-tablet clutter or surprise
              commission fees.
            </>
          }
          primaryCta={{
            href: '/#lead-form-anchor',
            label: 'Get Started',
            testId: 'ghost-hero-primary-cta',
          }}
          secondaryCta={{
            href: HREF_PRICING,
            label: 'See Pricing',
            testId: 'ghost-hero-secondary-cta',
          }}
        />

        <AnswerFirstBox
          title="How Hungry Canadian Scales Ghost Kitchens & Pop-Ups"
          testId="ghost-answer-first"
        >
          Hungry Canadian enables virtual food halls and ghost kitchens to host multiple brands
          under a single merchant storefront and tablet setup. With 1&ndash;2 business day
          onboarding,{' '}
          <InlineLink href={HREF_PRICING} testId="answer-link-flat-rate">
            flat-rate pricing
          </InlineLink>
          , multi-brand collection grids, time-stamped delivery dispatching, and rapid menu
          toggling, operators can test concepts and scale low-overhead operations seamlessly.
        </AnswerFirstBox>

        <div id="multi-brand">
          <ValueBlock {...BLOCK_MULTI_BRAND} testId="ghost-block-multi-brand" />
        </div>
        <div id="rapid-launch">
          <ValueBlock {...BLOCK_RAPID_LAUNCH} testId="ghost-block-rapid-launch" reverse />
        </div>
        <div id="driver-coordination">
          <ValueBlock {...BLOCK_DRIVER_OPS} testId="ghost-block-driver-ops" />
        </div>

        <VerticalFaq
          items={GHOST_FAQ}
          schema={FAQ_SCHEMA}
          testId="ghost-faq"
          eyebrow="Ghost Kitchens FAQ"
          description="Answers to the multi-brand aggregation, speed-to-launch, and driver-dispatch questions virtual food operators ask most before switching platforms."
        />

        <SpokeNav currentSlug="for-ghost-kitchens-popups" />
      </div>
    </PublicLayout>
  );
}
